import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axiosUtil';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import {
  NotificationView,
  copyToClipboard,
  notificationLink,
  notificationTarget,
  hasOwnPage,
} from 'components/NotificationView';

/**
 * A single notification at its own address: /notifications/:id (:id is Messages.id).
 *
 * This is what makes a notification linkable — shareable, and usable as the
 * "read it in Doctivity" link in an email — instead of "go to your dashboard and
 * click the bell". The bell is still there and unchanged; this is a second door
 * to the same thing.
 *
 * Rendered inside NotificationsPageContainer, which owns the green header bar and
 * the tabs — including the tab for this notification, which is how you get back to
 * the full list. Same shell as an activity note, so it doesn't need (and must not
 * duplicate) a page header of its own.
 *
 * Access is enforced server-side (see messagesService.getForUser). An unreachable
 * notification is never a dead end or a raw error: the user lands inside Doctivity,
 * signed in, is told which of the three cases it is, and is offered a way onward to
 * their own notifications.
 */
const UNAVAILABLE_COPY = {
  // The user deleted it from their list, it expired, or an admin switched it off.
  // Links are allowed to rot — following an old one says so rather than resurrecting
  // the notification.
  inactive: {
    title: 'This notification is no longer active',
    body: 'The link you used points to a notification that has since been removed, expired, '
      + 'or was deleted from your list. There is nothing to show for it any more.',
  },
  // Exists and is live, but was targeted at other people (e.g. another client).
  not_yours: {
    title: 'This notification wasn\'t sent to you',
    body: 'Notifications are only readable by the people they were sent to. If a colleague '
      + 'shared this link with you, ask a Doctivity admin to send the notification to you '
      + 'as well.',
  },
  not_found: {
    title: 'This notification is no longer active',
    body: 'The link you used doesn\'t point to a notification we can find. It may have been '
      + 'removed, or the link may be incomplete.',
  },
};

const NotificationPage = ({ onLoaded }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(null);
  const [toast, setToast] = useState('');
  // The "Opening your …" label while this page acts as a doorway to a note or
  // mileage log; null when it isn't. Doubles as the flag that keeps the page from
  // flashing the notification's own content on the way through.
  const [redirecting, setRedirecting] = useState(null);

  // Held in a ref so the container's setState callback isn't a dependency of
  // load() — otherwise reporting the loaded notification upward would re-create
  // load() and refetch on every render.
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => { onLoadedRef.current = onLoaded; });

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(null);
    setRedirecting(null);
    // Drop the previous notification's title straight away, so going list → a
    // different notification doesn't briefly label the new tab with the old one.
    if (onLoadedRef.current) onLoadedRef.current(null);
    try {
      const { data } = await axiosInstance.get(`/v2/my/notifications/${id}`);

      // A note/mileage assignment has no content of its own — it exists to point
      // at a work item. Arriving here from OUTSIDE the app (an emailed link, a
      // pasted URL) should end at that work item, not at a page describing it.
      // In-app the bell and the table already skip this page entirely; this is
      // the same behaviour for people coming in cold.
      //
      // This is what lets an email carry ONE url shape — /notifications/:id —
      // regardless of message type, and still land the reader on the end result.
      // It also means the email path goes through the read-marking below, which
      // a direct link to /notes/:id would bypass, leaving the bell unread forever.
      const leaving = !hasOwnPage(data);

      if (leaving) {
        const target = String(notificationTarget(data));
        setRedirecting(target.includes('/notes/mileage')
          ? 'Opening your mileage log…'
          : 'Opening your activity note…');
      } else {
        setNotification(data);
        // Gives the container a title for this notification's tab.
        if (onLoadedRef.current) onLoadedRef.current(data);
      }

      // Mark read here rather than server-side on the GET, so that an email
      // client prefetching the link (or a corporate URL scanner following it)
      // can never silently mark someone's notification as read. Awaited before
      // any redirect so the read is persisted before we navigate away.
      if (data && data.is_recipient && !data.read_at) {
        try {
          await axiosInstance.post(`/v2/my/notifications/${id}/read`);
        } catch (e) {
          /* non-fatal */
        }
      }

      if (leaving) {
        // replace: this page was a doorway, so Back should return wherever they
        // came from (or the email), not bounce them through here again.
        navigate(notificationTarget(data), { replace: true });
        return;
      }
    } catch (e) {
      const reason = e?.response?.data?.reason;
      setNotification(null);
      setUnavailable(UNAVAILABLE_COPY[reason] || UNAVAILABLE_COPY.not_found);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const copyLink = async () => {
    const ok = await copyToClipboard(notificationLink(notification));
    setToast(ok ? 'Link copied' : 'Could not copy the link');
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/v2/my/notifications/${id}`);
    } catch (e) {
      /* non-fatal */
    }
    navigate('/notifications');
  };

  return (
    <Box sx={{ px: 3 }}>
      <Card>
        <CardContent>
          {(loading || redirecting) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                py: 4,
              }}
            >
              <CircularProgress size={28} />
              {/* Only shown on the way through to a note/mileage log, so the
                  hand-off reads as intentional rather than as a stall. */}
              {redirecting && (
                <Typography variant='body2' color='textSecondary'>
                  {redirecting}
                </Typography>
              )}
            </Box>
          )}

          {!loading && !redirecting && unavailable && (
            <>
              <Typography component='h2' variant='h6' color='primary' sx={{ mb: '10px' }}>
                {unavailable.title}
              </Typography>
              <Typography color='textSecondary' sx={{ mb: 3 }}>
                {unavailable.body}
              </Typography>
              <Button variant='contained' color='primary' onClick={() => navigate('/notifications')}>
                Go to your notifications
              </Button>
            </>
          )}

          {!loading && !redirecting && !unavailable && notification && (
            <>
              {/* Full-width card like every other page, but the body stays a
                  readable column — Markdown stretched across a wide monitor is
                  unpleasant to read. */}
              <Box sx={{ maxWidth: 820 }}>
                <NotificationView notification={notification} />
              </Box>

              {/* All three actions grouped at the right, the way dialog actions
                  read: the two text buttons then Okay. No call-to-action button —
                  links live inside the body as Markdown (see NotificationView). */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                  mt: 3,
                  flexWrap: 'wrap',
                }}
              >
                {/* A note/mileage assignment reached by its own URL is still one
                    person's work item — nobody else can open the link, so don't
                    offer to copy it. See hasOwnPage. */}
                {hasOwnPage(notification) && (
                  <Button color='primary' startIcon={<LinkIcon />} onClick={copyLink}>
                    Copy link
                  </Button>
                )}
                {notification.is_recipient && (
                  <Button color='primary' onClick={handleDelete}>Delete</Button>
                )}
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => navigate('/notifications')}
                >
                  Okay
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

NotificationPage.propTypes = {
  onLoaded: PropTypes.func,
};

export { NotificationPage };
export default NotificationPage;
